import React from 'react';
import { Box, Typography } from '@mui/material';
import { SignalDisplayMode, SignalSlot, SignalsDefinition } from '../types';
import { ResolvedSlotStatus, resolveLiveStatuses } from '../resolveLiveStatus';

interface SignalsPreviewProps {
  signals?: SignalSlot[];
  definition?: SignalsDefinition | null;
  resolvedStatuses?: ResolvedSlotStatus[] | null;
  liveStatuses?: string | string[] | null;
  size?: 'normal' | 'compact' | 'tiny' | 'large';
  showLabels?: boolean;
  labelMode?: 'slot' | 'status';
  selectedSlotId?: string;
  onSelectSlot?: (slotId: string, el?: HTMLElement) => void;
  onCapClick?: (el: HTMLElement) => void;
  onBaseClick?: (el: HTMLElement) => void;
  capLabel?: string;
  baseLabel?: string;
  capActive?: boolean;
  baseColor?: string;
  /** Fill the parent; segment height flexes so the column fits the viewport. */
  fill?: boolean;
}

function modeClass(mode: SignalDisplayMode): string {
  switch (mode) {
    case 'off':
      return 'signal-off';
    case 'blink':
      return 'signal-blink';
    case 'fast_blink':
      return 'signal-fast-blink';
    case 'flash':
      return 'signal-flash';
    default:
      return 'signal-steady';
  }
}

export function signalSlotKey(
  status: { slotId?: string; slotName?: string; id?: string; name?: string },
  index: number,
): string {
  return status.slotId || status.id || status.slotName || status.name || `slot_${index}`;
}

const SIZE = {
  large: { colW: 132, segH: 72, gap: 1.25, py: 1, houseH: 26, radius: '16px' },
  normal: { colW: 88, segH: 44, gap: 1, py: 2, houseH: 18, radius: '12px' },
  compact: { colW: 36, segH: 18, gap: 0.35, py: 0.5, houseH: 10, radius: '6px' },
  tiny: { colW: 14, segH: 7, gap: 0.15, py: 0, houseH: 4, radius: '2px' },
} as const;

const HOUSING = '#2a2f36';

const SignalsPreview: React.FC<SignalsPreviewProps> = ({
  signals,
  definition,
  resolvedStatuses,
  liveStatuses,
  size = 'normal',
  showLabels = true,
  labelMode = 'slot',
  selectedSlotId,
  onSelectSlot,
  onCapClick,
  capActive,
  fill = false,
}) => {
  const slotList = definition?.signals || signals || [];
  const resolved: ResolvedSlotStatus[] = resolvedStatuses
    || resolveLiveStatuses(
      definition || { metadata: { version: '1', name: '', description: '' }, signals: slotList },
      liveStatuses,
      { preferPreviewWhenEmpty: true },
    );
  const ordered = [...resolved].reverse();
  const s = SIZE[size];
  const interactive = Boolean(onSelectSlot);
  const showAnyLabels = showLabels && size !== 'tiny';
  const capInteractive = Boolean(onCapClick);
  const colWidth = fill
    ? (showAnyLabels ? 'clamp(56px, 16vmin, 96px)' : 'clamp(72px, 22vmin, 140px)')
    : s.colW;
  const houseH = fill ? 20 : s.houseH;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: showAnyLabels ? 'row' : 'column',
        alignItems: fill && showAnyLabels ? 'stretch' : 'center',
        justifyContent: fill ? 'center' : undefined,
        gap: showAnyLabels ? 1.5 : (fill ? 0 : s.gap),
        py: fill ? 0 : s.py,
        width: fill ? '100%' : undefined,
        height: fill ? '100%' : undefined,
        minHeight: fill ? 0 : undefined,
        maxHeight: fill ? '100%' : undefined,
        overflow: fill ? 'hidden' : undefined,
        '@keyframes signalBlink': {
          '0%, 49%': { opacity: 1 },
          '50%, 100%': { opacity: 0.2 },
        },
        '@keyframes signalFastBlink': {
          '0%, 44%': { opacity: 1 },
          '45%, 100%': { opacity: 0.15 },
        },
        '@keyframes signalFlash': {
          '0%, 12%': { opacity: 1 },
          '13%, 100%': { opacity: 0.15 },
        },
        '& .signal-segment': { opacity: 0.25 },
        '& .signal-off': { opacity: 0.18 },
        '& .signal-steady': { opacity: 1 },
        '& .signal-blink': { animation: 'signalBlink 1s step-end infinite', opacity: 1 },
        '& .signal-fast-blink': { animation: 'signalFastBlink 0.35s step-end infinite', opacity: 1 },
        '& .signal-flash': { animation: 'signalFlash 1.1s step-end infinite', opacity: 1 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          width: colWidth,
          alignSelf: showAnyLabels ? undefined : 'center',
          flexShrink: 0,
          height: fill ? '100%' : undefined,
          minHeight: fill ? 0 : undefined,
          maxHeight: fill ? '100%' : undefined,
          boxSizing: 'border-box',
          bgcolor: HOUSING,
          borderRadius: s.radius,
          p: `${houseH * 0.35}px 5px`,
          pt: `${houseH}px`,
          pb: `${houseH}px`,
          gap: '3px',
          boxShadow: capActive
            ? 'inset 0 1px 0 rgba(255,255,255,0.18), 0 0 14px rgba(91,140,255,0.45)'
            : 'inset 0 1px 0 rgba(255,255,255,0.12), 0 1px 3px rgba(0,0,0,0.35)',
          filter: capActive ? 'brightness(1.12)' : undefined,
        }}
        onClick={capInteractive ? (e: React.MouseEvent<HTMLElement>) => {
          if (e.target !== e.currentTarget) return;
          onCapClick?.(e.currentTarget);
        } : undefined}
      >
        {ordered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: size === 'normal' ? 4 : 1 }}>
            —
          </Typography>
        ) : (
          ordered.map((status, index) => {
            const key = signalSlotKey(status, index);
            const color = status.color || '#000000';
            const cls = modeClass(status.mode);
            const selected = selectedSlotId === key || selectedSlotId === status.slotId;
            return (
              <Box
                key={key}
                component={interactive ? 'button' : 'div'}
                type={interactive ? 'button' : undefined}
                data-slot-id={key}
                title={`${status.slotName}: ${status.name}`}
                aria-label={`${status.slotName}: ${status.name}`}
                aria-pressed={interactive ? selected : undefined}
                className={`signal-segment ${cls}`}
                onClick={interactive ? (e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelectSlot?.(key, e.currentTarget);
                } : undefined}
                sx={{
                  all: 'unset',
                  display: 'block',
                  boxSizing: 'border-box',
                  flex: fill ? '1 1 0px' : `0 0 ${s.segH}px`,
                  width: '100%',
                  height: fill ? 0 : s.segH,
                  minHeight: fill ? 0 : s.segH,
                  overflow: 'hidden',
                  position: 'relative',
                  zIndex: 1,
                  borderRadius: '5px',
                  backgroundColor: color,
                  boxShadow: 'inset 0 2px 5px rgba(255,255,255,0.2), inset 0 -5px 8px rgba(0,0,0,0.25)',
                  outline: 'none',
                  filter: selected ? 'brightness(1.14)' : undefined,
                  cursor: interactive ? 'pointer' : 'default',
                  touchAction: 'manipulation',
                  '&:focus': { outline: 'none' },
                  '&:hover': interactive ? { filter: 'brightness(1.08)' } : undefined,
                }}
              />
            );
          })
        )}
      </Box>
      {showAnyLabels && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: fill ? 'stretch' : 'center',
            gap: '3px',
            py: `${houseH}px`,
            flex: fill ? '1 1 auto' : undefined,
            minWidth: 0,
            minHeight: fill ? 0 : undefined,
            height: fill ? '100%' : undefined,
            overflow: 'hidden',
          }}
        >
          {ordered.map((status, index) => {
            const key = signalSlotKey(status, index);
            const selected = selectedSlotId === key || selectedSlotId === status.slotId;
            return (
              <Typography
                key={`label-${key}`}
                variant="caption"
                color={selected ? 'text.primary' : 'text.secondary'}
                title={status.slotName}
                onClick={interactive ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelectSlot?.(key);
                } : undefined}
                sx={{
                  flex: fill ? '1 1 0px' : undefined,
                  height: fill ? 0 : s.segH,
                  minHeight: fill ? 0 : s.segH,
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: 1.2,
                  fontSize: size === 'compact' ? '0.65rem' : '0.8rem',
                  fontWeight: selected ? 600 : 400,
                  cursor: interactive ? 'pointer' : 'default',
                  overflow: 'hidden',
                }}
                noWrap
              >
                {labelMode === 'status' ? status.name : status.slotName}
              </Typography>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default SignalsPreview;
